import Table from 'Components/Table';
import React, { useState } from 'react';
import { Icon, Loading, Popover } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import { Column, Row } from 'react-table';
import { useSelector } from 'react-redux';
import { SCHEDULED_DEPLOYMENT_STATUS } from 'Common/enums';
import {
    shouldShowMerchants,
    shouldShowManageTenants,
    shouldShowTenantGroups,
    getUser,
    shouldShowScheduledDeployment
} from 'Selectors';
import { useNotificationsStore } from 'Hooks/zustand/useNotificationsStore';
import useStickyTableHead from 'Hooks/useStickyTableHead';
import useGetScheduledDeployment from 'Hooks/query-hooks/scheduled-deployment/useGetScheduledDeployment';
import type { ScheduledDeployment } from 'Hooks/query-hooks/scheduled-deployment/useGetScheduledDeployment';
import { RowActionDropdown } from 'Components/Table/RowActionDropdown';
import ScheduledDeploymentModal from 'Components/RulesHeader/ScheduledDeploymentModal';
// eslint-disable-next-line max-len
import type { ScheduledDeploymentModalProps } from 'Components/RulesHeader/ScheduledDeploymentModal/ScheduledDeploymentModal';
import styles from './RevisionsScheduledDeploymentTable.css';

type ReactTableColumn = Column<ScheduledDeployment>;

type FilteredColumns = ReactTableColumn[];
type AllColumns = Array<ReactTableColumn & { isHidden?: boolean }>;

const TABLE_ID = 'revisions-scheduled-deployment-table';

const RevisionsScheduledDeploymentTable = () => {
    const { t } = useTranslation();
    const { landlordId, tenantKey } = useSelector(getUser);
    const [isOpen, setIsOpen] = useState(false);
    const scheduledDeployment = useGetScheduledDeployment({ options: { polling: true } });
    const { operation } = useNotificationsStore.getState().scheduledDeployment;
    const setOperation = useNotificationsStore((state) => state.setOperation);

    const scheduledDeploymentModalProps: ScheduledDeploymentModalProps = {
        closeModal: () => {
            setIsOpen(false);
        },
        modal: {
            id: 'scheduled-deployment-modify-modal',
            name: 'scheduled-deployment-modify-modal',
            title: 'modal title',
            operation
        },
        options: { isOpen },
        status: 'warning'
    };

    const windowParams = [
        'top=0',
        'left=0',
        `height=${window?.screen?.height}`,
        `width=${window?.screen?.width}`,
        'fullscreen=yes'
    ].join(',');

    const openWindow = (id: number) => {
        const windowUrl = `/revisions/scheduled/${id}`;
        window?.open(windowUrl, windowUrl, windowParams);
    };

    /** TODO: This need to be removed - Please use CSS instead! */
    useStickyTableHead(TABLE_ID);

    if (scheduledDeployment.isLoading) return <Loading size='sm' />;

    if (!scheduledDeployment.data) return null;

    const allColumns: AllColumns = [
        {
            Header: '',
            id: '_status',
            accessor: (data) => data.status,
            width: 50,
            maxWidth: 50,
            renderType: 'overrideCell',
            Cell: ({ row }: { row: Row }) => {
                if (row.values._status === 'failed') {
                    return (
                        <Popover
                            showOnHover
                            content={t('Deployment failed')}
                            showArrow
                            position='top-start'
                            contentClassName={styles.popOverContentStyle}
                            arrowClassName={styles.popperArrowStyle}
                        >
                            <Icon name='small-error' />
                        </Popover>
                    );
                }
                return null;
            },
            isHidden: !shouldShowScheduledDeployment()
        },
        {
            Header: 'Version ID',
            accessor: 'version',
            id: 'version',
            renderType: 'link',
            renderOptions: {
                to: 'revisions/scheduled/$0',
                openNewWindow: true,
                newWindowParams: windowParams,
                iconName: 'expand'
            },
            style: {
                width: '8%'
            },
            disableSortBy: true,
            dataType: 'number'
        },
        {
            Header: 'Status',
            accessor: (data) => (data.status ? t(SCHEDULED_DEPLOYMENT_STATUS[data.status]) : '--'),
            id: 'status',
            disableSortBy: true,
            dataType: 'text',
            style: {
                width: '8%'
            },
            renderType: 'overrideCell',
            Cell: ({ row }: { row: Row }) => {
                if (row.values._status === 'failed') {
                    return (
                        <span className={styles.failedLabel}>
                            {t('Deployment')} {t(row.values.status)}
                        </span>
                    );
                }
                return <span>{t(row.values.status)}</span>;
            }
        },
        {
            Header: 'Comment',
            accessor: (data) => data.comment ?? '--',
            id: 'comment',
            disableSortBy: true,
            dataType: 'text',
            style: {
                width: '8%'
            }
        },
        {
            Header: 'Scheduled for',
            accessor: 'scheduledTime',
            id: 'scheduledTime',
            dataType: 'datetime',
            sortType: 'datetime',
            renderType: 'datetime',
            disableSortBy: true,
            style: {
                width: '8%'
            }
        },
        {
            Header: 'Scheduled by',
            accessor: (data) => `${data.userInfo.lastName}, ${data.userInfo.firstName}`,
            id: 'userInfo',
            disableSortBy: true,
            dataType: 'text',
            style: {
                width: '8%'
            }
        },
        {
            Header: 'New Global Rules',
            accessor: (data) => data.globalRules.added,
            id: 'globalRules-added',
            dataType: 'number',
            disableSortBy: true,
            style: {
                width: '8%'
            }
        },
        {
            Header: 'Modified Global Rules',
            accessor: (data) => data.globalRules.modified,
            id: 'globalRules-modified',
            dataType: 'number',
            disableSortBy: true,
            style: {
                width: '8%'
            }
        },
        {
            Header: t('New Merchants'),
            accessor: (data) => data.merchants.added,
            id: 'merchants-added',
            disableSortBy: true,
            dataType: 'number',
            style: {
                width: '8%'
            },
            isHidden: !shouldShowMerchants()
        },
        {
            Header: t('Modified Merchants'),
            accessor: (data) => data.merchants.modified,
            id: 'merchants-modified',
            disableSortBy: true,
            dataType: 'number',
            style: {
                width: '8%'
            },
            isHidden: !shouldShowMerchants()
        },
        {
            Header: t('Deactivated Merchants'),
            accessor: (data) => data.merchants.disabled,
            id: 'merchants-disabled',
            disableSortBy: true,
            dataType: 'number',
            style: {
                width: '8%'
            },
            isHidden: !shouldShowMerchants()
        },
        {
            Header: t('New Tenant Groups'),
            accessor: (data) => data.tenantGroups.added,
            id: 'tenantGgroups-added',
            style: {
                width: '7%'
            },
            dataType: 'number',
            isHidden: !shouldShowManageTenants(landlordId, tenantKey) && shouldShowTenantGroups()
        },
        {
            Header: t('Modified Tenant Groups'),
            accessor: (data) => data.tenantGroups.modified,
            id: 'tenantGgroups-modified',
            style: {
                width: '7%'
            },
            dataType: 'number',
            isHidden: !shouldShowManageTenants(landlordId, tenantKey) && shouldShowTenantGroups()
        },
        {
            accessor: 'id',
            id: 'modify-scheduled-deployment',
            disableSortBy: true,
            width: 40,
            maxWidth: 40,
            renderType: 'overrideCell',
            Cell: ({ value: id, row }: { value: string; row: Row }) => {
                if (row.values._status === 'in_progress') {
                    return null;
                }
                return (
                    <RowActionDropdown
                        menuId={`${id}-action-dropdown`}
                        menuActions={
                            [
                                {
                                    testId: 'edit-scheduled-deployment',
                                    label: t('Edit'),
                                    handleAction: (event: React.MouseEvent) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        setOperation('update');
                                        setIsOpen(true);
                                    }
                                },
                                {
                                    testId: 'cancel-scheduled-deployment',
                                    label: t('Cancel'),
                                    handleAction: (event: React.MouseEvent) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        setOperation('delete');
                                        setIsOpen(true);
                                    }
                                }
                            ] as any
                        }
                    />
                );
            }
        }
    ];

    const filteredColumns = allColumns.reduce((columns: FilteredColumns, column) => {
        if (!column.isHidden) {
            const { isHidden, ...resultColumn } = column;
            columns.push(resultColumn);
        }
        return columns;
    }, []);

    return (
        <div className={styles.tableContainer}>
            <Table
                tableName='scheduled-deployment-table'
                dataTestId={TABLE_ID}
                columns={filteredColumns}
                clickableRow
                data={[scheduledDeployment.data]}
                innerTableHeader={t('Scheduled')}
                onRowInteractCB={(row) => {
                    openWindow(row?.values?.version);
                }}
            />
            <ScheduledDeploymentModal
                closeModal={scheduledDeploymentModalProps.closeModal}
                modal={scheduledDeploymentModalProps.modal}
                options={scheduledDeploymentModalProps.options}
                status={scheduledDeploymentModalProps.status}
            />
        </div>
    );
};

export default RevisionsScheduledDeploymentTable;
